import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  Button,
  Typography,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Breadcrumbs,
  Link,
  Collapse,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PrintIcon from "@mui/icons-material/Print";
import EmailIcon from "@mui/icons-material/Email";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoIcon from "@mui/icons-material/Info";
import ParkIcon from "@mui/icons-material/LocalParking";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TextField from "@mui/material/TextField";

// API & Helpers
import {
  getOrdersbyid,
  getProducts,
  patchOrder,
  patchProduct,
  postLoan,
  getOrderTransitions,
  executeTransition,
} from "../../helpers/api_helper";
import { insertDocuments } from "../../helpers/aedevat";
import { printInvoice } from "./PrintInvoice";
import { sendEmailOffer } from "../../helpers/generalfunctions";
import { erpTableColors } from "../../theme/flexflowTheme";
import { fetchInvoiceCompanies } from "../../store/invoiceCompanies/actions";
import {
  selectActiveInvoiceCompanies,
  selectInvoiceCompaniesLoading,
} from "../../store/invoiceCompanies/reducer";
import { getInvoiceCompanyDecrypted } from "../../helpers/api_helper";

// Status configuration
const STATUS_CONFIG = {
  "PENDING-STOCK": { label: "Pending Stock", color: "#f59e0b", bg: "#fef3c7" },
  "STOCK-OK": { label: "Stock OK", color: "#3b82f6", bg: "#dbeafe" },
  "READY-TO-INVOICE": { label: "Ready to Invoice", color: "#8b5cf6", bg: "#ede9fe" },
  "ON SHIPPING": { label: "On Shipping", color: "#f97316", bg: "#ffedd5" },
  "INVOICED": { label: "Invoiced", color: "#06b6d4", bg: "#cffafe" },
  "OFFER": { label: "Offer", color: "#6366f1", bg: "#e0e7ff" },
  "SHIPPED": { label: "Shipped", color: "#10b981", bg: "#d1fae5" },
  "RETURNED": { label: "Returned", color: "#ef4444", bg: "#fee2e2" },
  "PARKED": { label: "Parked", color: "#6b7280", bg: "#f3f4f6" },
  "CANCELED": { label: "Canceled", color: "#6b7280", bg: "#f3f4f6" },
};

// EU Countries for VAT exemption
const EU_COUNTRIES = [
  "Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czech Republic",
  "Denmark", "Estonia", "Finland", "France", "Germany", "Hungary",
  "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg", "Malta",
  "Netherlands", "Poland", "Portugal", "Romania", "Slovakia", "Slovenia",
  "Spain", "Sweden",
];

const MuiOrderDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  const { customers } = useSelector((state) => ({
    customers: state.customers.customers,
  }));

  // Invoice companies from Redux
  const invoiceCompanies = useSelector(selectActiveInvoiceCompanies);
  const invoiceCompaniesLoading = useSelector(selectInvoiceCompaniesLoading);

  document.title = `${t("Order")} #${params?.id || ""} | FlexFlow ERP`;

  // State
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [products, setProducts] = useState([]);
  const [loanProducts, setLoanProducts] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(true);

  // Dialogs
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [updateAADE, setUpdateAADE] = useState(true);

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editedProducts, setEditedProducts] = useState([]);

  // Workflow transitions
  const [transitions, setTransitions] = useState([]);
  const [transitionsLoading, setTransitionsLoading] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Get status config
  const statusConfig = useMemo(() => {
    return STATUS_CONFIG[order?.status] || { label: order?.status, color: "#6b7280", bg: "#f3f4f6" };
  }, [order?.status]);

  // Fetch invoice companies on mount
  useEffect(() => {
    dispatch(fetchInvoiceCompanies());
  }, [dispatch]);

  // Fetch order data
  useEffect(() => {
    const fetchOrder = async () => {
      if (!params?.id) {
        navigate("/orders");
        return;
      }

      setLoading(true);
      try {
        const res = await getOrdersbyid(params.id);
        if (res && res[0]) {
          const orderData = res[0];
          setOrder(orderData);

          const customerData = customers.find((c) => c.id === orderData.customer_id);
          setCustomer(customerData || {});

          setProducts(JSON.parse(orderData.products || "[]"));
          setLoanProducts(JSON.parse(orderData.loans || "[]"));
          setTotals(JSON.parse(orderData.totals || "{}"));
        } else {
          showSnackbar(t("Order not found"), "error");
          navigate("/orders");
        }
      } catch (error) {
        console.error("Error fetching order:", error);
        showSnackbar(t("Error fetching order"), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [params, customers, navigate]);

  // Fetch available transitions when order loads
  useEffect(() => {
    const fetchTransitions = async () => {
      if (!order?.id) return;

      setTransitionsLoading(true);
      try {
        const result = await getOrderTransitions(order.id);
        setTransitions(result || []);
      } catch (error) {
        console.error("Error fetching transitions:", error);
        // Fall back to empty transitions - will use legacy buttons
        setTransitions([]);
      } finally {
        setTransitionsLoading(false);
      }
    };

    fetchTransitions();
  }, [order?.id]);

  // Execute workflow transition
  const handleExecuteTransition = async (transition) => {
    setSaving(true);
    try {
      const result = await executeTransition(order.id, transition.name, {});
      if (result?.success) {
        showSnackbar(t("Transition completed successfully"));
        navigate("/orders");
      } else {
        showSnackbar(result?.message || t("Transition failed"), "error");
      }
    } catch (error) {
      console.error("Error executing transition:", error);
      showSnackbar(error.message || t("Error executing transition"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Show snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Update order status
  const updateOrderStatus = async (status) => {
    setSaving(true);
    try {
      const payload = {
        resource: [{ id: order.id, status }],
      };
      const res = await patchOrder(payload);
      if (res && res.resource?.[0]) {
        showSnackbar(t("Order status updated"));
        navigate("/orders");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showSnackbar(t("Error updating order status"), "error");
    } finally {
      setSaving(false);
    }
  };

  // ─── Edit Mode Handlers ────────────────────────────────────
  const handleEnterEditMode = () => {
    setEditedProducts(JSON.parse(JSON.stringify(products)));
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedProducts([]);
  };

  const handleEditField = (index, field, value) => {
    setEditedProducts(prev => prev.map((p, i) => {
      if (i !== index) return p;
      const updated = { ...p, [field]: value };
      // Recalculate line totals when price, quantity, or vat change
      if (field === "salesprice" || field === "quantity" || field === "vat") {
        const price = parseFloat(field === "salesprice" ? value : updated.salesprice) || 0;
        const qty = parseInt(field === "quantity" ? value : updated.quantity) || 1;
        const vat = parseFloat(field === "vat" ? value : updated.vat) || 0;
        updated.totalPrdAmmount = (price * qty).toFixed(2);
        updated.vatProducts = (price * qty * vat / 100).toFixed(2);
      }
      return updated;
    }));
  };

  const handleRemoveProduct = (index) => {
    setEditedProducts(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const totalProductsAmmount = editedProducts.reduce((s, p) => s + parseFloat(p.totalPrdAmmount || 0), 0);
      const totalVat = editedProducts.reduce((s, p) => s + parseFloat(p.vatProducts || 0), 0);
      const totalItems = editedProducts.reduce((s, p) => s + (parseInt(p.quantity) || 1), 0);
      const newTotals = {
        totalProductsAmmount,
        totalVat,
        totalItems,
        totalOrder: (totalProductsAmmount + totalVat).toFixed(2),
      };

      const payload = {
        resource: [{
          id: order.id,
          products: JSON.stringify(editedProducts),
          totals: JSON.stringify(newTotals),
        }],
      };

      const res = await patchOrder(payload);
      if (res && res.resource?.[0]) {
        setProducts(editedProducts);
        setTotals(newTotals);
        setEditMode(false);
        setEditedProducts([]);
        showSnackbar(t("Order updated successfully"));
      }
    } catch (error) {
      console.error("Error saving order edits:", error);
      showSnackbar(t("Error saving order"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Check if customer is EU (outside Greece)
  const isEuCustomer = useMemo(() => {
    const country = customer?.country || "";
    return EU_COUNTRIES.includes(country) && country !== "Greece";
  }, [customer?.country]);

  // Send to invoice with company-specific settings
  const sendToInvoice = async (company) => {
    setSaving(true);
    try {
      let updatedProducts = products;
      let updatedTotals = totals;

      // Determine refcode and VAT handling
      let refcode = company.refcode;
      let applyVat = company.applies_vat;

      // Handle EU customer VAT exemption
      if (isEuCustomer && company.eu_enabled) {
        refcode = company.eu_refcode || company.refcode;
        applyVat = false; // EU transactions are VAT exempt
      }

      // Remove VAT if company doesn't apply VAT or EU exemption
      if (!applyVat) {
        updatedProducts = products.map((prd) => ({ ...prd, vatProducts: 0 }));
        updatedTotals = {
          ...totals,
          totalOrder: totals.totalOrder - totals.totalVat,
          totalVat: 0,
        };
        setProducts(updatedProducts);
        setTotals(updatedTotals);
      }

      const payload = {
        resource: [
          {
            id: order.id,
            status: "INVOICED",
            refcode: refcode,
            products: JSON.stringify(updatedProducts),
            totals: JSON.stringify(updatedTotals),
            invoicedAt: company.short_name || company.name,
            invoice_company_id: company.id,
          },
        ],
      };

      // Handle Epsilon integration if enabled
      if (company.epsilon_enabled) {
        try {
          // Get decrypted credentials for Epsilon
          const decryptedCompany = await getInvoiceCompanyDecrypted(company.id);
          if (decryptedCompany) {
            // Pass company config to insertDocuments
            await insertDocuments(order, customer, {
              epsilonUrl: decryptedCompany.epsilon_url,
              epsilonUsername: decryptedCompany.epsilon_username,
              epsilonPassword: decryptedCompany.epsilon_password,
              vatExemptionCode: isEuCustomer && company.eu_enabled ? company.eu_vat_exemption_code : null,
            });
          }
        } catch (epsilonError) {
          console.error("Epsilon integration error:", epsilonError);
          // Continue with order update even if Epsilon fails
          showSnackbar(t("Warning: Epsilon integration failed, but order will be invoiced"), "warning");
        }
      }

      // Handle e-timologiera (AADE/MyData) integration if enabled
      if (company.etimologiera_enabled) {
        // TODO: Implement e-timologiera API call
        console.log("e-timologiera integration pending implementation");
      }

      const res = await patchOrder(payload);
      if (res && res.resource?.[0]) {
        setShipDialogOpen(false);
        showSnackbar(t("Order invoiced successfully"));
        navigate("/orders");
      }
    } catch (error) {
      console.error("Error invoicing order:", error);
      showSnackbar(t("Error sending order"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Cancel order
  const cancelOrder = async () => {
    setSaving(true);
    try {
      // Restore product stock
      for (const prd of products) {
        const productData = await getProducts(prd.id);
        const currentStock = productData?.[0]?.stock || 0;
        await patchProduct({
          resource: [{ id: prd.id, stock: currentStock + parseInt(prd.quantity, 10) }],
        });
      }

      // Reverse loan records
      for (const loanPrd of loanProducts) {
        await postLoan({
          resource: [
            {
              bound: "INBOUND",
              custname: customer?.name,
              quantity: loanPrd.quantity,
              customer_id: customer?.id,
              order_id: order.id,
              prd_id: loanPrd.id,
              prdname: loanPrd.name,
            },
          ],
        });
      }

      await updateOrderStatus("CANCELED");
      setCancelDialogOpen(false);
    } catch (error) {
      console.error("Error canceling order:", error);
      showSnackbar(t("Error canceling order"), "error");
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Alert severity="error">{t("Order not found")}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Compact Header: Breadcrumb + Status + Actions */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          border: "1px solid #e5e7eb",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => navigate("/orders")} sx={{ color: "#6b7280" }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator="/" sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5 } }}>
            <Link component={RouterLink} to="/" sx={{ display: "flex", alignItems: "center", color: "#6b7280", textDecoration: "none", fontSize: "0.813rem" }}>
              <HomeIcon sx={{ fontSize: 16 }} />
            </Link>
            <Link component={RouterLink} to="/orders" sx={{ color: "#6b7280", textDecoration: "none", fontSize: "0.813rem" }}>
              {t("Orders")}
            </Link>
            <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#111827" }}>
              #{order.id}
            </Typography>
          </Breadcrumbs>
          <Chip
            label={t(statusConfig.label)}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.688rem",
              fontWeight: 600,
              bgcolor: statusConfig.bg,
              color: statusConfig.color,
              border: `1px solid ${statusConfig.color}`,
            }}
          />
        </Stack>

        {/* Quick Info */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <PersonIcon sx={{ fontSize: 14, color: "#6b7280" }} />
            <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
              {customer?.name || "-"}
            </Typography>
            {customer?.id && (
              <IconButton
                size="small"
                onClick={() => navigate(`/customer-detail/${customer.id}`)}
                sx={{
                  p: 0.5,
                  ml: 0.5,
                  color: "#3b82f6",
                  bgcolor: "#dbeafe",
                  "&:hover": { bgcolor: "#bfdbfe" },
                  width: 22,
                  height: 22,
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Stack>
          <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
            <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
            {order.expected_delivery_date || "-"}
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#111827" }}>
            {Number(totals.totalOrder || 0).toFixed(2)}€
          </Typography>
        </Stack>
      </Paper>

      {/* Expandable Info Panel */}
      <Paper
        elevation={0}
        sx={{ mb: 1.5, borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden" }}
      >
        <Box
          onClick={() => setInfoExpanded(!infoExpanded)}
          sx={{
            px: 2,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            bgcolor: infoExpanded ? "#f9fafb" : "#fff",
            "&:hover": { bgcolor: "#f9fafb" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoIcon sx={{ fontSize: 18, color: "#6b7280" }} />
            <Typography sx={{ fontSize: "0.813rem", fontWeight: 500, color: "#374151" }}>
              {t("Order Details")}
            </Typography>
          </Stack>
          <IconButton size="small" sx={{ p: 0.25 }}>
            {infoExpanded ? <ExpandLessIcon sx={{ fontSize: 20, color: "#6b7280" }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: "#6b7280" }} />}
          </IconButton>
        </Box>
        <Collapse in={infoExpanded}>
          <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
            <Grid container spacing={2}>
              {/* Customer Info */}
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                  {t("Customer")}
                </Typography>
                <Box sx={{ bgcolor: "#f9fafb", p: 1.5, borderRadius: 1, border: "1px solid #e5e7eb" }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#111827" }}>{customer?.name}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{customer?.label}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", mt: 0.5 }}>{customer?.address}, {customer?.city}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("VAT")}: {customer?.vat}</Typography>
                </Box>
              </Grid>

              {/* Order Info */}
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                  {t("Order Info")}
                </Typography>
                <Box sx={{ bgcolor: "#f9fafb", p: 1.5, borderRadius: 1, border: "1px solid #e5e7eb" }}>
                  <Stack spacing={0.5}>
                    <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("Created")}: <strong style={{ color: "#111827" }}>{order.created}</strong></Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("Delivery")}: <strong style={{ color: "#111827" }}>{order.expected_delivery_date || "-"}</strong></Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("Updated")}: <strong style={{ color: "#111827" }}>{order.updated}</strong></Typography>
                    {order.shipping_by && <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("Driver")}: <strong style={{ color: "#111827" }}>{order.shipping_by}</strong></Typography>}
                  </Stack>
                </Box>
              </Grid>

              {/* Totals Summary */}
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280", mb: 1, textTransform: "uppercase" }}>
                  {t("Summary")}
                </Typography>
                <Box sx={{ bgcolor: "#f9fafb", p: 1.5, borderRadius: 1, border: "1px solid #e5e7eb" }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("Items")}</Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#111827" }}>{products.length} ({totals.totalItems || 0} {t("qty")})</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("Subtotal")}</Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#111827" }}>{Number(totals.totalProductsAmmount || 0).toFixed(2)}€</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("VAT")}</Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#111827" }}>{Number(totals.totalVat || 0).toFixed(2)}€</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.5, borderTop: "1px solid #e5e7eb" }}>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#111827" }}>{t("Total")}</Typography>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#10b981" }}>{Number(totals.totalOrder || 0).toFixed(2)}€</Typography>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Action Buttons Row */}
      <Paper elevation={0} sx={{ px: 2, py: 1, mb: 1.5, borderRadius: 1, border: "1px solid #e5e7eb" }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
          {/* Dynamic Workflow Transitions from Backend */}
          {transitions.length > 0 ? (
            <>
              {transitionsLoading ? (
                <CircularProgress size={20} />
              ) : (
                transitions.map((transition) => (
                  <Button
                    key={transition.name}
                    size="small"
                    variant={transition.primary ? "contained" : "outlined"}
                    color={transition.color || "primary"}
                    onClick={() => {
                      // Handle special transitions that need dialogs
                      if (transition.name === "invoice" || transition.name === "invoice_ship") {
                        setShipDialogOpen(true);
                      } else if (transition.name === "cancel" || transition.name === "reject") {
                        setCancelDialogOpen(true);
                      } else {
                        handleExecuteTransition(transition);
                      }
                    }}
                    disabled={saving}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      bgcolor: transition.primary && transition.buttonColor ? transition.buttonColor : undefined,
                      "&:hover": transition.primary && transition.buttonColor ? {
                        bgcolor: transition.buttonColor,
                        opacity: 0.9
                      } : undefined,
                    }}
                  >
                    {t(transition.label)}
                  </Button>
                ))
              )}
            </>
          ) : (
            <>
              {/* Legacy Static Buttons - Fallback when workflow API not available */}
              {/* Invoiced Status Actions */}
              {order.status === "INVOICED" && (
                <Button size="small" variant="contained" startIcon={<ReceiptIcon />} onClick={() => setInvoiceDialogOpen(true)} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                  {t("View Invoice")}
                </Button>
              )}

              {/* On Shipping Status Actions */}
              {order.status === "ON SHIPPING" && (
                <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={() => updateOrderStatus("SHIPPED")} disabled={saving} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                  {t("Complete Order")}
                </Button>
              )}

              {/* Offer Status Actions */}
              {order.status === "OFFER" && (
                <>
                  {editMode ? (
                    <>
                      <Button size="small" variant="contained" color="success" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={handleSaveEdit} disabled={saving} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                        {saving ? t("Saving...") : t("Save Changes")}
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<CloseIcon />} onClick={handleCancelEdit} disabled={saving} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                        {t("Cancel")}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={handleEnterEditMode} disabled={saving} sx={{ textTransform: "none", fontSize: "0.75rem", bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}>
                        {t("Edit Order")}
                      </Button>
                      <Button size="small" variant="contained" startIcon={<ShoppingCartIcon />} onClick={() => updateOrderStatus("PENDING-STOCK")} disabled={saving} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                        {t("Convert to Order")}
                      </Button>
                      <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => updateOrderStatus("OFFER-DELETE")} disabled={saving} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                        {t("Delete Offer")}
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={() => printInvoice(order)} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                        {t("Print")}
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<EmailIcon />} onClick={() => sendEmailOffer(order)} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                        {t("Send Email")}
                      </Button>
                    </>
                  )}
                </>
              )}

              {/* Ready to Invoice Status Actions */}
              {order.status === "READY-TO-INVOICE" && (
                <>
                  <Button size="small" variant="contained" color="success" startIcon={<ReceiptIcon />} onClick={() => setShipDialogOpen(true)} disabled={saving} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                    {t("Invoice & Ship")}
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<ParkIcon />} onClick={() => updateOrderStatus("PARKED")} disabled={saving} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                    {t("Park Order")}
                  </Button>
                  <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => setCancelDialogOpen(true)} disabled={saving} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                    {t("Reject Order")}
                  </Button>
                </>
              )}

              {/* Returned Status Actions */}
              {order.status === "RETURNED" && (
                <>
                  <Chip label={t("Order returned by driver")} size="small" color="warning" sx={{ fontSize: "0.688rem" }} />
                  <Button size="small" variant="contained" startIcon={<LocalShippingIcon />} onClick={() => updateOrderStatus("INVOICED")} disabled={saving} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                    {t("Re-Ship")}
                  </Button>
                  <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => setCancelDialogOpen(true)} disabled={saving} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                    {t("Cancel Order")}
                  </Button>
                </>
              )}
            </>
          )}
        </Stack>
      </Paper>

      {/* Products Table */}
      <Paper elevation={0} sx={{ borderRadius: 1, border: `1px solid ${editMode ? "#3b82f6" : erpTableColors.products.headerBorder}`, overflow: "hidden" }}>
        <Box sx={{ px: 2, py: 1, bgcolor: editMode ? "#eff6ff" : erpTableColors.products.headerBg, borderBottom: `1px solid ${erpTableColors.products.headerBorder}` }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: editMode ? "#1d4ed8" : erpTableColors.products.headerText, textTransform: "uppercase" }}>
            {t("Products")} ({(editMode ? editedProducts : products).length})
            {editMode && ` — ${t("Editing")}`}
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: erpTableColors.products.headerBg }}>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.products.headerText, textTransform: "uppercase", py: 1 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.products.headerText, textTransform: "uppercase", py: 1 }}>{t("Product")}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.products.headerText, textTransform: "uppercase", py: 1 }}>{t("SKU")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.products.headerText, textTransform: "uppercase", py: 1 }}>{t("Price")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.products.headerText, textTransform: "uppercase", py: 1 }}>{t("VAT")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.products.headerText, textTransform: "uppercase", py: 1 }}>{t("Qty")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.products.headerText, textTransform: "uppercase", py: 1 }}>{t("Subtotal")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.products.headerText, textTransform: "uppercase", py: 1 }}>{t("VAT Amt")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.products.headerText, textTransform: "uppercase", py: 1 }}>{t("Total")}</TableCell>
                {editMode && <TableCell sx={{ width: 40, py: 1 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {(editMode ? editedProducts : products).map((prd, i) => {
                if (editMode) {
                  const lineTotal = (parseFloat(prd.totalPrdAmmount) || 0) + (parseFloat(prd.vatProducts) || 0);
                  return (
                    <TableRow key={i} sx={{ bgcolor: "#fefce8" }}>
                      <TableCell sx={{ fontSize: "0.75rem", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{i + 1}</TableCell>
                      <TableCell sx={{ py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>
                        <TextField value={prd.name || ""} onChange={(e) => handleEditField(i, "name", e.target.value)} size="small" variant="standard" sx={{ minWidth: 120, "& input": { fontSize: "0.78rem" } }} />
                      </TableCell>
                      <TableCell sx={{ py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>
                        <TextField value={prd.sku || ""} onChange={(e) => handleEditField(i, "sku", e.target.value)} size="small" variant="standard" sx={{ width: 90, "& input": { fontSize: "0.75rem", fontFamily: "monospace" } }} />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>
                        <TextField value={prd.salesprice || ""} onChange={(e) => handleEditField(i, "salesprice", e.target.value)} size="small" variant="standard" type="number" sx={{ width: 75, "& input": { fontSize: "0.75rem", textAlign: "right" } }} />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>
                        <TextField value={prd.vat || ""} onChange={(e) => handleEditField(i, "vat", e.target.value)} size="small" variant="standard" type="number" sx={{ width: 50, "& input": { fontSize: "0.75rem", textAlign: "right" } }} />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>
                        <TextField value={prd.quantity || ""} onChange={(e) => handleEditField(i, "quantity", e.target.value)} size="small" variant="standard" type="number" inputProps={{ min: 1 }} sx={{ width: 50, "& input": { fontSize: "0.75rem", textAlign: "right" } }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.75rem", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{Number(prd.totalPrdAmmount || 0).toFixed(2)}€</TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.75rem", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{Number(prd.vatProducts || 0).toFixed(2)}€</TableCell>
                      <TableCell align="right" sx={{ fontSize: "0.813rem", fontWeight: 600, py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{lineTotal.toFixed(2)}€</TableCell>
                      <TableCell sx={{ py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>
                        <IconButton size="small" onClick={() => handleRemoveProduct(i)} sx={{ color: "#dc2626" }}>
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                }
                return (
                  <TableRow key={i} sx={{ "&:hover": { bgcolor: erpTableColors.row.hoverBg } }}>
                    <TableCell sx={{ fontSize: "0.75rem", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontSize: "0.813rem", fontWeight: 500, py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{prd.name}</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", color: "#6b7280", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{prd.sku}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.75rem", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{Number(prd.salesprice).toFixed(2)}€</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.75rem", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{Number(prd.vat).toFixed(0)}%</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.75rem", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{Number(prd.quantity).toFixed(0)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.75rem", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{Number(prd.totalPrdAmmount).toFixed(2)}€</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.75rem", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{Number(prd.vatProducts).toFixed(2)}€</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.813rem", fontWeight: 600, py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{(Number(prd.totalPrdAmmount) + Number(prd.vatProducts)).toFixed(2)}€</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals Footer */}
        {(() => {
          const displayTotals = editMode
            ? {
                totalProductsAmmount: editedProducts.reduce((s, p) => s + parseFloat(p.totalPrdAmmount || 0), 0),
                totalVat: editedProducts.reduce((s, p) => s + parseFloat(p.vatProducts || 0), 0),
                get totalOrder() { return this.totalProductsAmmount + this.totalVat; },
              }
            : totals;
          return (
            <Box sx={{ px: 2, py: 1.5, bgcolor: erpTableColors.products.headerBg, borderTop: `1px solid ${erpTableColors.products.headerBorder}` }}>
              <Stack direction="row" justifyContent="flex-end" spacing={4}>
                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("Subtotal")}: <strong style={{ color: "#111827" }}>{Number(displayTotals.totalProductsAmmount || 0).toFixed(2)}€</strong></Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>{t("VAT")}: <strong style={{ color: "#111827" }}>{Number(displayTotals.totalVat || 0).toFixed(2)}€</strong></Typography>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#10b981" }}>{t("Total")}: {Number(displayTotals.totalOrder || 0).toFixed(2)}€</Typography>
              </Stack>
            </Box>
          );
        })()}
      </Paper>

      {/* Loaned Products */}
      {loanProducts.length > 0 && (
        <Paper elevation={0} sx={{ mt: 1.5, borderRadius: 1, border: `1px solid ${erpTableColors.loans.headerBorder}`, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1, bgcolor: erpTableColors.loans.headerBg, borderBottom: `1px solid ${erpTableColors.loans.headerBorder}` }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: erpTableColors.loans.headerText, textTransform: "uppercase" }}>
              {t("Loaned Products")} ({loanProducts.length})
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: erpTableColors.loans.headerBg }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.loans.headerText, textTransform: "uppercase", py: 1 }}>{t("SKU")}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.loans.headerText, textTransform: "uppercase", py: 1 }}>{t("Name")}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.688rem", color: erpTableColors.loans.headerText, textTransform: "uppercase", py: 1 }}>{t("Qty")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loanProducts.map((prd, i) => (
                  <TableRow key={i} sx={{ "&:hover": { bgcolor: erpTableColors.row.hoverBg } }}>
                    <TableCell sx={{ fontSize: "0.75rem", color: "#6b7280", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{prd.sku}</TableCell>
                    <TableCell sx={{ fontSize: "0.813rem", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{prd.name}</TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.75rem", py: 0.75, borderBottom: `1px solid ${erpTableColors.row.borderColor}` }}>{prd.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Ship/Invoice Dialog */}
      <Dialog
        open={shipDialogOpen}
        onClose={() => setShipDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <LocalShippingIcon color="primary" />
            {t("Confirm Shipping")}
          </Box>
        </DialogTitle>
        <DialogContent>
          {customer?.customfields && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("Customer Information")}
              </Typography>
              {JSON.parse(customer.customfields).map((field, index) => (
                <Typography key={index} variant="body2">
                  <strong>{field.name}:</strong> {field.value}
                </Typography>
              ))}
            </Box>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={updateAADE}
                onChange={(e) => setUpdateAADE(e.target.checked)}
                color="primary"
              />
            }
            label={t("Update AADE")}
            sx={{ mb: 2 }}
          />
          {isEuCustomer && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              {t("EU customer detected. VAT exemption will be applied for companies with EU handling enabled.")}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("Select invoicing method")}:
          </Typography>
          {invoiceCompaniesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : invoiceCompanies.length === 0 ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {t("No invoice companies configured. Please add invoice companies in Settings.")}
            </Alert>
          ) : (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {invoiceCompanies
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                .map((company) => (
                  <Button
                    key={company.id}
                    variant="contained"
                    onClick={() => sendToInvoice(company)}
                    disabled={saving}
                    sx={{
                      flex: 1,
                      minWidth: 120,
                      bgcolor: company.button_color || "#3b82f6",
                      "&:hover": {
                        bgcolor: company.button_color || "#3b82f6",
                        opacity: 0.9,
                      },
                      borderRadius: 2,
                    }}
                  >
                    {saving ? (
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    ) : (
                      <>
                        {company.short_name || company.name}
                        {company.is_default && " *"}
                      </>
                    )}
                  </Button>
                ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShipDialogOpen(false)}>{t("Cancel")}</Button>
        </DialogActions>
      </Dialog>

      {/* Invoice View Dialog */}
      <Dialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ReceiptIcon color="primary" />
            {t("Invoice")}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2">{t("Customer")}: {customer?.label}</Typography>
            <Typography variant="body2">
              {t("Address")}: {customer?.address}, {customer?.city} {customer?.postcode}
            </Typography>
            <Typography variant="body2">{t("VAT")}: {customer?.vat}</Typography>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell>{t("SKU")}</TableCell>
                  <TableCell>{t("Product")}</TableCell>
                  <TableCell align="right">{t("Qty")}</TableCell>
                  <TableCell align="right">{t("VAT")}</TableCell>
                  <TableCell align="right">{t("Price")}</TableCell>
                  <TableCell align="right">{t("Total")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((prd, i) => (
                  <TableRow key={i}>
                    <TableCell>{prd.sku || "-"}</TableCell>
                    <TableCell>{prd.name}</TableCell>
                    <TableCell align="right">{prd.quantity}</TableCell>
                    <TableCell align="right">{prd.vat || "-"}%</TableCell>
                    <TableCell align="right">{prd.salesprice || "-"}€</TableCell>
                    <TableCell align="right">{prd.totalPrdAmmount || "-"}€</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>{t("Total Items")}:</strong> {totals.totalItems}
              </Typography>
              <Typography variant="body2">
                <strong>{t("Products Amount")}:</strong> {totals.totalProductsAmmount}€
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>{t("Total VAT")}:</strong> {totals.totalVat}€
              </Typography>
              <Typography variant="h6">
                <strong>{t("Total")}:</strong> {totals.totalOrder}€
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDialogOpen(false)}>{t("Close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CancelIcon color="error" />
            {t("Cancel Order")}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("This action cannot be undone.")}
          </Alert>
          <Typography>
            {t("Are you sure you want to cancel this order?")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("Product stock will be restored and loan records will be reversed.")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>{t("Keep Order")}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={cancelOrder}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <CancelIcon />}
          >
            {t("Cancel Order")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MuiOrderDetail;
